import { useState, useMemo, useEffect, useContext } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import DropdownInput from '../components/inputs/DropdownInput'
import DropdownMenu from '../components/menu/DropdownMenu'
import FormInput from '../components/inputs/FormInput'
import MenuItem from '@mui/material/MenuItem'
import UsersTable from '../components/tables/UsersTable'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrowDown from '../assets/arrow-down.png'
import actions from '../assets/actions.png'
import detailsIcon from '../assets/details-icon.png'
import editIcon from '../assets/edit-icon.png'
import enableIcon from '../assets/enable-icon.png'
import disableIcon from '../assets/disable-icon.png'
import deleteIcon from '../assets/delete-icon.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'
import '../res/toastStyles.css'

const UsersBO = () => {
    const [selectedProfile, setSelectedProfile] = useState(Constants.PROFILES.find(profile => profile.value === Constants.Roles.CLIENT))
    const [clients, setClients] = useState([])
    const [dealers, setDealers] = useState([])
    const [portTechnicians, setPortTechnicians] = useState([])
    const [portAdmins, setPortAdmins] = useState([])

    const [selectedUser, setSelectedUser] = useState(null)
    const [filterText, setFilterText] = useState('')

    const [showDisableUserModal, setShowDisableUserModal] = useState(false)
    const [showEnableUserModal, setShowEnableUserModal] = useState(false)
    const [showRemoveUserModal, setShowRemoveUserModal] = useState(false)

    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const [anchorElActions, setAnchorElActions] = useState(null)
    const openActions = Boolean(anchorElActions)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role === Constants.Roles.PORTADMIN || loggedUser.role === Constants.Roles.ADMINIT) {
                if(loggedUser.role === Constants.Roles.PORTADMIN) {
                    setSelectedProfile(Constants.PROFILES.find(profile => profile.value === Constants.Roles.PORTTECHNICIAN))
                }

                getAllUsers()
            } else {
                navigate('/')
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get users request
    const getUsers = (roleId) => {
        fetch(`/api/Backoffice/List?roleId=${roleId}`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
    
                    switch(roleId) {
                        case Constants.Roles.CLIENT: setClients(jsonRes)
                        break
    
                        case Constants.Roles.DEALER: setDealers(jsonRes)
                        break
    
                        case Constants.Roles.PORTTECHNICIAN: setPortTechnicians(jsonRes)
                        break
    
                        case Constants.Roles.PORTADMIN: setPortAdmins(jsonRes)
                        break
    
                        default: return
                    }
                  
                } else {
                    switch(roleId) {
                        case Constants.Roles.CLIENT: setClients([]); showErrorToast(strings.getUsersError + strings.client)
                        break
    
                        case Constants.Roles.DEALER: setDealers([]); showErrorToast(strings.getUsersError + strings.dealer)
                        break
    
                        case Constants.Roles.PORTTECHNICIAN: setPortTechnicians([]); showErrorToast(strings.getUsersError + strings.harborTechnician)
                        break
    
                        case Constants.Roles.PORTADMIN: setPortAdmins([]); showErrorToast(strings.getUsersError + strings.harborAdmin)
                        break
    
                        default: return
                    }
                }
            }, 1000)
 
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                switch(roleId) {
                    case Constants.Roles.CLIENT: setClients([])
                    break

                    case Constants.Roles.DEALER: setDealers([])
                    break

                    case Constants.Roles.PORTTECHNICIAN: setPortTechnicians([])
                    break

                    case Constants.Roles.PORTADMIN: setPortAdmins([])
                    break

                    default: return
                }
            }, 1000) 
        })
    }

    // Handle get clients
    const getClients = () => {
        getUsers(Constants.Roles.CLIENT)
    }

    // Handle get dealers
    const getDealers = () => {
        getUsers(Constants.Roles.DEALER)
    }

    // Handle get port technicians
    const getPortTechnicians = () => {
        getUsers(Constants.Roles.PORTTECHNICIAN)
    }

    // Handle get port admins
    const getPortAdmins = () => {
        getUsers(Constants.Roles.PORTADMIN)
    }

    // Handle get all users
    const getAllUsers = () => {
        getClients()
        getDealers()
        getPortTechnicians()
        getPortAdmins()
    }

    const columnsClient = useMemo(() => [
        {
            name: strings.name2,
            selector: row => row.name,
            sortable: true
        },
        {
            name: strings.nif,
            selector: row => row.nif,
            sortable: true
        },
        {
            name: strings.email,
            cell: row => row.email,
            selector: row => row.email,
            sortable: true
        },
        {
            name: strings.boats,
            selector: row => row.numberBoats,
            sortable: true
        },
        {
            name: strings.state,
            selector: row => row.isDeleted ? strings.deleted : row.active ? strings.active : strings.inactive,
            sortable: true
        },
        {
            name: strings.actions,
            cell: row => actionCell(row),
            button: true
        }
    ], [])

    const columnsDealer = useMemo(() => [
        {
            name: strings.user,
            selector: row => row.name,
            sortable: true
        },
        {
            name: strings.email,
            cell: row => row.email,
            selector: row => row.email,
            sortable: true
        },
        {
            name: strings.entity,
            selector: row => row.entityName,
            sortable: true
        },
        {
            name: strings.harbor,
            selector: row => row.harborName,
            sortable: true
        },
        {
            name: strings.state,
            selector: row => row.active ? strings.active : strings.inactive,
            sortable: true
        },
        {
            name: strings.actions,
            cell: row => actionCell(row),
            button: true
        }
    ], [])

    const columnsTechnician = useMemo(() => [
        {
            name: strings.name2,
            selector: row => row.name,
            sortable: true
        },
        {
            name: strings.email,
            selector: row => row.email,
            sortable: true
        },
        {
            name: strings.harbor,
            selector: row => row.harborName,
            sortable: true
        },
        {
            name: strings.state,
            selector: row => row.active ? strings.active : strings.inactive,
            sortable: true
        },
        {
            name: strings.actions,
            cell: row => actionCell(row),
            button: true
        }
    ], [])

    const actionCell = (row) => {
        return (
            row.isDeleted ? <>-</>
            :   <div className='flex flex-row items-center'>
                    <button onClick={(event) => openActionsDropdownMenu(event, row)}>
                        <img className='h-6 w-6' src={actions}  alt='Expandir menu de ações' />
                    </button>
                </div> 
        )
    }

    const conditionalRowStyles = [
        {
          when: row => row.active === false,
          style: {
            color: '#A9ACAF'
          }
        }
    ]

    // Handle changes in search input text
    const onChangeSearch = (searchText) => {
        setFilterText(searchText.target.value)
    }

    // Handles open add user dropdown menu
    const openDropdownMenu = (event) => {
        setAnchorEl(event.currentTarget)
    }

    // Handles close add user dropdown menu
    const closeDropdownMenu = () => {
        setAnchorEl(null)
    }

    // Navigate to new user harbor technician
    const onClickHarborTechnician = () => {
        setAnchorEl(null)
        navigate('/new-harbor-technician')
    }

    // Navigate to new user harbor admin
    const onClickHarborAdmin = () => {
        setAnchorEl(null)
        navigate('/new-harbor-admin')
    }

    // Navigate to new user dealer
    const onClickDealer = () => {
        setAnchorEl(null)
        navigate('/new-dealer')
    }

    // Handles open actions dropdown menu
    const openActionsDropdownMenu = (event, row) => {
        setAnchorElActions(event.currentTarget)
        setSelectedUser(row)
    }

    // Handles close actions dropdown menu
    const closeActionsDropdownMenu = () => {
        setAnchorElActions(null)
        setSelectedUser(null)
    }

    // Navigate to user details
    const onClickDetails = () => {
        setAnchorElActions(null)
        navigate(`/user-details/${selectedUser.id}`)
    }

    // Navigate to update user
    const onClickUpdate = () => {
        setAnchorElActions(null)
  
        switch(selectedProfile.value) {
            case 1: navigate(`/edit-client/${selectedUser.id}`)
            break
            
            case 2: navigate(`/edit-dealer/${selectedUser.id}`) 
            break

            case 3: navigate(`/edit-harbor-technician/${selectedUser.id}`)
            break

            case 4: navigate(`/edit-harbor-admin/${selectedUser.id}`)
            break

            default: return
        }
    }

    // Show disable user modal
    const onClickDisable = () => {
        setAnchorElActions(null)
        setShowDisableUserModal(true)
    }

    // Handles disabled user
    const disableUser = () => {
        setShowDisableUserModal(false)
        setIsLoading(true)

        // Disable user
        fetch(`/api/BackOffice/${selectedUser.id}/DeactivateUser`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
              setIsLoading(false)
    
              if (response.status === 200) {
                setIsLoading(true)
                getAllUsers()
              } else if (response.status === 404) {
                showErrorToast(strings.userNotFound)
              } else if (response.status === 401) {
                showErrorToast(strings.notAuthorized)
              } else {
                showErrorToast(strings.disableUserError)
              }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
              setIsLoading(false)
              showErrorToast(error)
            }, 1000)
        })  
    }

    // Show enable user modal
    const onClickEnable = () => {
        setAnchorElActions(null)
        setShowEnableUserModal(true)
    }

    // Handles enable user
    const enableUser = () => {
        setShowEnableUserModal(false)
        setIsLoading(true)

        // Enable user
        fetch(`/api/BackOffice/${selectedUser.id}/ActivateUser`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
              setIsLoading(false)
    
              if (response.status === 200) {
                setIsLoading(true)
                getAllUsers()
              } else if (response.status === 404) {
                showErrorToast(strings.userNotFound)
              } else if (response.status === 401) {
                showErrorToast(strings.notAuthorized)
              } else {
                showErrorToast(strings.enableUserError)
              }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
              setIsLoading(false)
              showErrorToast(error)
            }, 1000)
        })
    }

    // Show remove user modal
    const onClickRemove = () => {
        setAnchorElActions(null)
        setShowRemoveUserModal(true)
    }

    // Handles remove user
    const removeUser = () => {
        setShowRemoveUserModal(false)
        setIsLoading(true)

        // Remove user
        fetch(`/api/BackOffice/${selectedUser.id}/DeleteUser`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
              setIsLoading(false)
    
              if (response.status === 200) {
                setIsLoading(true)
                getAllUsers()
              } else if (response.status === 404) {
                showErrorToast(strings.userNotFound)
              } else {
                showErrorToast(strings.deleteUserError)
              }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
              setIsLoading(false)
              showErrorToast(error)
            }, 1000)
        })
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='h-14 border-b border-accent flex flex-row items-center justify-between px-[12.5rem]'>
                <div className='flex flex-row space-x-9'>
                    <Link to='/backoffice-schedules' className='font-montserrat text-neutral5 cursor-pointer'>{strings.schedules}</Link>
                    <p className='font-montserrat font-semibold text-accent'>{strings.userManagement}</p>

                    { loggedUser && loggedUser.role === Constants.Roles.ADMINIT  ?
                        <Link to='/backoffice-entities' className='font-montserrat text-neutral5 cursor-pointer'>{strings.entityManagement}</Link>
                    : null }
                   
                </div>
                
                { loggedUser && loggedUser.role === Constants.Roles.PORTADMIN ? 
                    <Link to='/new-harbor-technician' className='flex flex-row items-center rounded-[6.25rem] h-10 ps-6 pe-6 bg-accent'>
                        <p className='font-montserrat font-semibold text-white'>{strings.addHarborTechnician}</p>
                    </Link>
                :   <div className='flex flex-row items-center'>
                        <button onClick={openDropdownMenu} className='flex flex-row items-center rounded-[6.25rem] h-10 ps-6 pe-4 bg-accent space-x-2.5'>
                            <p className='font-montserrat font-semibold text-white'>{strings.addUser}</p>
                            <img className='h-5 w-5' src={arrowDown} alt='Expandir menu' />
                        </button>

                        <DropdownMenu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={closeDropdownMenu}
                        >
                            <MenuItem onClick={onClickDealer} disableRipple>{strings.dealer}</MenuItem>
                            <MenuItem onClick={onClickHarborTechnician} disableRipple>{strings.harborTechnician}</MenuItem>
                            <MenuItem onClick={onClickHarborAdmin} disableRipple>{strings.harborAdmin}</MenuItem>
                        </DropdownMenu>
                    </div>
                }

            </div>

            <div className='flex flex-row px-[12.5rem] my-8 space-x-6'>
                { loggedUser && loggedUser.role === Constants.Roles.PORTADMIN ? null
                :   <div className='flex flex-col w-1/5'>
                        <p className='font-montserrat font-semibold text-sm text-navy'>{strings.profile}</p>
                        <DropdownInput 
                            options={Constants.PROFILES}
                            value={selectedProfile}
                            onChange={(item) => setSelectedProfile(item)}
                        />
                    </div>
                }
                
                <div className='flex flex-1 flex-col'>
                    <label htmlFor='filter' className='font-montserrat font-semibold text-sm text-navy'>{strings.search}</label>
                    <FormInput 
                        id='filter' 
                        type='text'
                        value={filterText}
                        placeholder={strings.enterSearchTerms}
                        onChange={onChangeSearch}
                    />
                </div>
            </div>

            <div className='min-h-screen px-[12.5rem] mb-16'>
                { isLoading ?
                    <LoadingModal />
                :   selectedProfile.value === 1 ?
                        clients.length > 0 ?
                            <UsersTable
                                columns={columnsClient}
                                data={clients.filter(
                                    item => (item.name && item.name.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.email && item.email.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.numberBoats.toString() && item.numberBoats.toString().toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.active && item.active === true ? strings.active.toLowerCase().includes(filterText.toLowerCase()) : strings.inactive.toLowerCase().includes(filterText.toLowerCase()))
                                )}
                                conditionalRowStyles={conditionalRowStyles}
                            />
                        :   <p className='font-montserrat text-neutral5 text-center'>{strings.noInfo}</p>

                    : selectedProfile.value === 2 ?
                        dealers.length > 0 ?
                            <UsersTable
                                columns={columnsDealer}
                                data={dealers.filter(
                                    item => (item.name && item.name.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.email && item.email.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.entityName && item.entityName.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.harborName && item.harborName.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.active && item.active === true ? strings.active.toLowerCase().includes(filterText.toLowerCase()) : strings.inactive.toLowerCase().includes(filterText.toLowerCase()))
                                )}
                                conditionalRowStyles={conditionalRowStyles}
                            />
                        :   <p className='font-montserrat text-neutral5 text-center'>{strings.noInfo}</p>  
                        
                    : selectedProfile.value === 3 ?
                        portTechnicians.length > 0 ?
                            <UsersTable
                                columns={columnsTechnician}
                                data={portTechnicians.filter(
                                    item => (item.name && item.name.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.email && item.email.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.harborName && item.harborName.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.active && item.active === true ? strings.active.toLowerCase().includes(filterText.toLowerCase()) : strings.inactive.toLowerCase().includes(filterText.toLowerCase()))
                                )}
                                conditionalRowStyles={conditionalRowStyles}
                            />
                        :   <p className='font-montserrat text-neutral5 text-center'>{strings.noInfo}</p>

                    :   portAdmins.length > 0 ?
                            <UsersTable
                                columns={columnsTechnician}
                                data={portAdmins.filter(
                                    item => (item.name && item.name.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.email && item.email.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.harborName && item.harborName.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.active && item.active === true ? strings.active.toLowerCase().includes(filterText.toLowerCase()) : strings.inactive.toLowerCase().includes(filterText.toLowerCase()))
                                )}
                                conditionalRowStyles={conditionalRowStyles}
                            />
                        :   <p className='font-montserrat text-neutral5 text-center'>{strings.noInfo}</p>
                }
            </div>

            {selectedUser ?
                <DropdownMenu
                    anchorEl={anchorElActions}
                    open={openActions}
                    onClose={closeActionsDropdownMenu}
                >
                    <MenuItem onClick={onClickDetails} disableRipple>
                        <img className='h-[1.125rem] w-[1.125] me-3' src={detailsIcon} alt='Ver detalhes' />
                        {strings.seeDetails}
                    </MenuItem>

                    {!selectedUser.active ? null
                    :   <MenuItem onClick={onClickUpdate} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={editIcon} alt='Editar utilizador' />
                            {strings.updateUser}
                        </MenuItem>
                    }     

                    {!selectedUser.active ?
                        <MenuItem onClick={onClickEnable} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={enableIcon} alt='Ativar utilizador' />
                            {strings.enableUser}
                        </MenuItem>
                    :   <MenuItem onClick={onClickDisable} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={disableIcon} alt='Desativar utilizador' />
                            {strings.disableUser}
                        </MenuItem>
                    }
            
                    <MenuItem onClick={onClickRemove} disableRipple style={{ color: '#E85F5C' }}>
                        <img className='h-[1.125rem] w-[1.125] me-3' src={deleteIcon} alt='Remover utilizador' />
                        {strings.removeUser}
                    </MenuItem>
                    
                </DropdownMenu>
            : null}

            <TwoButtonsModal 
                open={showDisableUserModal}
                onClose={() => setShowDisableUserModal(false)}
                title={strings.disableUser}
                message={strings.disableUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.disable}
                onClick={disableUser}
            />

            <TwoButtonsModal 
                open={showEnableUserModal}
                onClose={() => setShowEnableUserModal(false)}
                title={strings.enableUser}
                message={strings.enableUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.enable}
                onClick={enableUser}
            />

            <TwoButtonsModal 
                open={showRemoveUserModal}
                onClose={() => setShowRemoveUserModal(false)}
                title={strings.removeUser}
                message={strings.removeUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.remove}
                onClick={removeUser}
            />

            <ToastContainer />
        </>
    )
}

export default UsersBO